/*
 * Kidicare - ISO 22196:2011 Certified Antimicrobial Surface Upgrade
 *
 * One rule, enforced after every cart change:
 *   upgrade line quantity === total quantity of eligible lines marked antimicrobial
 *
 * The quantity is recalculated from the cart itself each time, so it self-heals.
 * Quantity edits, deletions, a second table added later, or a customer deleting
 * the upgrade line by hand all converge back to the rule. Nothing is paired, so
 * nothing can drift.
 */
(function () {
  'use strict';

  var CFG = window.KIDICARE_ISO || {};
  var VARIANT = Number(CFG.variantId || 0);
  var MARK = CFG.mark || 'Antimicrobial (ISO 22196:2011)';
  var PROP = 'Surface';

  if (!VARIANT) return;

  var root =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var base = root.replace(/\/$/, '');

  var reconciling = false;

  function api(path, body) {
    return fetch(base + path, {
      method: body ? 'POST' : 'GET',
      headers: body
        ? { 'Content-Type': 'application/json', Accept: 'application/json' }
        : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'same-origin'
    }).then(function (r) {
      return r.json();
    });
  }

  /* ---- 1. Inject the checkbox into the product form ---- */

  function buildCheckbox() {
    var wrap = document.createElement('div');
    wrap.className = 'iso-upgrade';
    wrap.setAttribute('data-iso-upgrade', '');

    // Visible strings come from the Liquid-rendered EN/FR config
    // (window.KIDICARE_ISO.text, set by snippets/antimicrobial-upgrade.liquid
    // from request.locale.iso_code). The literals below are only the fallback
    // if that config is ever missing.
    var T = CFG.text || {};
    var label = T.label || 'Upgrade to an antimicrobial surface';
    var subCopy =
      T.subCopy ||
      'Tested to ISO 22196:2011, the international method for measuring antibacterial activity on non-porous surfaces.';
    var linkText = T.linkText || 'What this means';
    var tableOnlyNote =
      T.tableOnlyNote ||
      'Applies to the table surface only. The chairs included in this set do not have the antimicrobial surface.';

    // Table & chairs sets: the certified surface is the table only. Required by
    // the campaign strategy doc - no listing may imply the chairs are certified.
    var scope = CFG.tableOnly
      ? '<small class="iso-upgrade__scope">' + tableOnlyNote + '</small>'
      : '';

    wrap.innerHTML =
      '<label class="iso-upgrade__row">' +
      '<input type="checkbox" name="iso_upgrade" value="1" class="iso-upgrade__box">' +
      '<span class="iso-upgrade__text">' +
      '<strong>' +
      label +
      ' &mdash; +' +
      (CFG.priceLabel || '$49.99') +
      '</strong>' +
      '<small class="iso-upgrade__sub">' +
      subCopy +
      ' ' +
      '<a href="' +
      (CFG.landing || '#') +
      '" class="iso-upgrade__link">' +
      linkText +
      '</a></small>' +
      '</span>' +
      '</label>' +
      scope +
      '<input type="hidden" name="properties[' +
      PROP +
      ']" value="Standard" data-iso-prop>';
    return wrap;
  }

  // This theme renders more than one /cart/add form on a product page (a
  // sticky/secondary buy form alongside the main one). Only the main form gets
  // the checkbox, and only ever one per page.
  function pickForm() {
    var forms = Array.prototype.slice.call(
      document.querySelectorAll('form[action*="/cart/add"]')
    );

    var candidates = forms.filter(function (form) {
      if (!form.querySelector('[name="id"]')) return false;
      if (form.closest('.card, .product-card, .quick-view')) return false;
      // The main buy form is the one with a quantity selector.
      if (!form.querySelector('[name="quantity"]')) return false;
      var btn = form.querySelector('[name="add"], [type="submit"]');
      if (!btn) return false;
      // Visible only - skips forms hidden behind a closed drawer.
      return btn.offsetParent !== null;
    });

    return candidates[0] || null;
  }

  function mount() {
    if (!CFG.eligible) return;
    // Single instance per page, whatever the theme re-renders.
    if (document.querySelector('[data-iso-upgrade]')) return;

    var form = pickForm();
    if (!form) return;

    var btn = form.querySelector('[name="add"], [type="submit"]');
    var anchor = btn.closest('.product-form__buttons') || btn;
    if (anchor.parentNode) {
      anchor.parentNode.insertBefore(buildCheckbox(), anchor);
    } else {
      form.appendChild(buildCheckbox());
    }
  }

  /* ---- 2. Checkbox writes the property onto the parent line ---- */

  document.addEventListener('change', function (e) {
    var box = e.target;
    if (!box.matches || !box.matches('[data-iso-upgrade] input[name="iso_upgrade"]')) {
      return;
    }
    var wrap = box.closest('[data-iso-upgrade]');
    var prop = wrap && wrap.querySelector('[data-iso-prop]');
    if (prop) prop.value = box.checked ? MARK : 'Standard';
  });

  /* ---- 3. Reconcile the upgrade quantity against the cart ---- */

  function reconcile() {
    if (reconciling) return Promise.resolve(null);
    reconciling = true;

    return api('/cart.js')
      .then(function (cart) {
        var wanted = 0;
        var current = 0;

        (cart.items || []).forEach(function (item) {
          if (item.variant_id === VARIANT) {
            current += item.quantity;
            return;
          }
          var props = item.properties || {};
          if (props[PROP] === MARK) wanted += item.quantity;
        });

        if (wanted === current) return null;

        // /cart/update.js sets an absolute quantity by variant id - exactly the
        // semantics we want, including 0 to remove the line.
        var updates = {};
        updates[VARIANT] = wanted;
        return api('/cart/update.js', { updates: updates });
      })
      .catch(function (err) {
        console.error('[iso-upgrade] reconcile failed', err);
        return null;
      })
      .then(function (res) {
        reconciling = false;
        return res;
      });
  }

  /* ---- 4. Run it after anything that changes the cart ---- */

  var nativeFetch = window.fetch;
  window.fetch = function () {
    var args = arguments;
    var url = String((args[0] && args[0].url) || args[0] || '');
    return nativeFetch.apply(this, args).then(function (res) {
      var isCartWrite = /\/cart\/(add|change|update|clear)(\.js)?/.test(url);
      if (isCartWrite && res.ok && !reconciling) {
        reconcile().then(function (changed) {
          if (changed) refreshCartUI();
        });
      }
      return res;
    });
  };

  function refreshCartUI() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    if (window.location.pathname.indexOf('/cart') === 0) {
      window.location.reload();
    }
  }

  /* ---- 5. Boot ---- */

  function boot() {
    mount();
    reconcile();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', mount);

  window.KidicareISOUpgrade = { reconcile: reconcile, mount: mount };
})();
